import React, { useState } from "react";
import { DialogBody, DialogFooter } from "components/common/dialogs/GenericDialog";
import { useLocale } from "lib/hooks/useLocale";
import NotPlacedImg from "assests/images/order-failed.svg";
import CheckoutItems from "components/checkout/CheckoutItems";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { putRequest } from "lib/core/apiClient";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { errorToast } from "lib/utils/toasters";
import { appendParamsToItemsUrl, getItems } from "lib/utils/checkout";
import { CheckoutActions, ShippingHandlesType } from "lib/types/checkout";
import { redirectUrl } from "lib/utils/helpers";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { mutate } from "swr";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { apiURI } from "lib/utils/constants";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

interface OOSDialogProps {}

const OOSDialog: React.FC<OOSDialogProps> = () => {
  const { t } = useLocale();
  const { openPopup, closePopup } = usePopupOrchestrator();
  const {
    state: { merchant },
  } = useMerchantContext();

  const {
    state: { oosItems, checkoutId, isC2P, actionUrls },
    actions: {
      setShippingHandles,
      setCheckoutView,
      setCheckoutModal,
      setCheckoutItems,
      updateCheckoutBasedOnCheckoutResponse,
    },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleOOSClick = async () => {
    try {
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      setIsLoading(true);
      const response: any = await putRequest(`/checkout/${checkoutId}/checkout-items`, {}, "KRATOS_PRIVATE");
      mutate(!Boolean(isC2P) ? DISCOUNT_PATH : null);
      mutate(`/checkout/${checkoutId}/rewards`);
      mutatePayment();
      const items = getItems(response.items);
      setCheckoutItems(items);
      handleCheckoutResponse(response);
      if (!Boolean(response?.pricing?.serviceable)) {
        errorToast(t("serviceability_error"), 5000);
        setCheckoutView("ADDRESS_LIST");
        return;
      }
      setIsLoading(false);

      const isAddressServiceable = response?.pricing?.serviceable ?? false;
      if (!Boolean(isAddressServiceable)) {
        errorToast(t("serviceability_error"), 5000);
        return;
      }

      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      setShippingHandles(shippingHandles as ShippingHandlesType);
      if (shippingHandles.length > 0 && Boolean(showShippingHandles)) {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }
      if (response?.metadata?.action_urls?.[CheckoutActions.ADDRESS_SELECT]?.success_url) {
        redirectUrl(response.metadata.action_urls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }

      if (
        actionUrls &&
        actionUrls[CheckoutActions.ADDRESS_SELECT] &&
        actionUrls[CheckoutActions.ADDRESS_SELECT].success_url
      ) {
        redirectUrl(actionUrls[CheckoutActions.ADDRESS_SELECT].success_url);
        return;
      }
      closePopup();
      setCheckoutView("PAYMENTS");
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      closePopup();
      //Exit checkout if items go OOS
      if (merchant) {
        redirectUrl(`${merchant.brandUrl}/?floRedirectStatus=oos`);
      }
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse, "MAIN", true);
  };

  if (!Boolean(oosItems?.length)) return <></>;
  return (
    <>
      <div className="flex flex-col h-full pt-4">
        <div className="flex flex-col items-center flex-1 mb-20">
          <img src={NotPlacedImg} alt="oos_leaf" className="h-10 w-10" />
          <h1 className="pt-6 text-xl font-medium text-carbon-dark">{t("oos_body_header")}</h1>
          <p className="pt-2 pb-3 text-sm text-coal-dark">{t("oos_body_subheader")}</p>
          <div className="flex-1 overflow-y-scroll max-h-96">
            {Boolean(oosItems) && <CheckoutItems items={oosItems ?? []} disableOOSItems={true} />}
          </div>
        </div>

        <DialogFooter>
          <div className="px-4">
            <PrimaryButton
              buttonText={t("oos_cta")}
              onClick={handleOOSClick}
              isCheckout={false}
              isLoading={isLoading}
            />
          </div>
        </DialogFooter>
      </div>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default OOSDialog;
